import type { LocalSession, LocalUser } from "./types";

type Filter = { op: "eq"; column: string; value: unknown };
type Order = { column: string; ascending?: boolean };
type QueryResult<T = unknown> = { data: T | null; error: { message: string } | null; count?: number | null };
type AuthCallback = (event: string, session: LocalSession | null) => void;

const API_BASE = import.meta.env.VITE_LOCAL_BACKEND_URL || "/api/local";
const SESSION_KEY = "cloud-local-session";
const listeners = new Set<AuthCallback>();

function storedSession(): LocalSession | null {
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem(SESSION_KEY);
  if (!raw) return null;
  try {
    return JSON.parse(raw) as LocalSession;
  } catch {
    return null;
  }
}

function setStoredSession(session: LocalSession | null) {
  if (typeof window === "undefined") return;
  if (session) window.localStorage.setItem(SESSION_KEY, JSON.stringify(session));
  else window.localStorage.removeItem(SESSION_KEY);
  listeners.forEach((callback) => callback(session ? "SIGNED_IN" : "SIGNED_OUT", session));
}

async function request<T>(path: string, init: RequestInit = {}): Promise<{ data: T | null; error: { message: string } | null }> {
  if (typeof window === "undefined" && API_BASE.startsWith("/")) {
    return { data: null, error: { message: "Local backend is only available in the browser for relative API URLs" } };
  }
  try {
    const session = storedSession();
    const headers = new Headers(init.headers);
    if (typeof init.body === "string" && !headers.has("content-type")) headers.set("content-type", "application/json");
    if (session?.access_token) headers.set("authorization", `Bearer ${session.access_token}`);
    const response = await fetch(`${API_BASE}${path}`, { ...init, headers });
    const contentType = response.headers.get("content-type") || "";
    const data = contentType.includes("application/json") ? await response.json().catch(() => null) : null;
    if (!response.ok) return { data: null, error: { message: data?.error || "Request failed" } };
    if (data === null) return { data: null, error: { message: "Local backend returned a non-JSON response" } };
    return { data: data as T, error: null };
  } catch (error) {
    return { data: null, error: { message: error instanceof Error ? error.message : "Request failed" } };
  }
}

class QueryBuilder<T = any> implements PromiseLike<QueryResult<T[]>> {
  private filters: Filter[] = [];
  private orders: Order[] = [];
  private selectColumns = "*";
  private selectOptions: { count?: "exact"; head?: boolean } = {};
  private writeMethod: "select" | "insert" | "update" | "delete" | "upsert" = "select";
  private values: unknown;

  constructor(private table: string) {}

  select(columns = "*", options: { count?: "exact"; head?: boolean } = {}) {
    this.selectColumns = columns;
    this.selectOptions = options;
    return this;
  }

  eq(column: string, value: unknown) {
    this.filters.push({ op: "eq", column, value });
    return this;
  }

  order(column: string, options: { ascending?: boolean } = {}) {
    this.orders.push({ column, ascending: options.ascending });
    return this;
  }

  insert(values: unknown) {
    this.writeMethod = "insert";
    this.values = values;
    return this;
  }

  update(values: unknown) {
    this.writeMethod = "update";
    this.values = values;
    return this;
  }

  upsert(values: unknown) {
    this.writeMethod = "upsert";
    this.values = values;
    return this;
  }

  delete() {
    this.writeMethod = "delete";
    return this;
  }

  async maybeSingle(): Promise<QueryResult<T | null>> {
    const result = await this.execute<T[]>();
    if (result.error) return { data: null, error: result.error, count: result.count };
    return { data: (result.data?.[0] as T | undefined) ?? null, error: null, count: result.count };
  }

  async single(): Promise<QueryResult<T>> {
    const result = await this.execute<T[]>();
    if (result.error) return { data: null, error: result.error, count: result.count };
    const row = result.data?.[0] as T | undefined;
    return row ? { data: row, error: null, count: result.count } : { data: null, error: { message: "No rows returned" }, count: result.count };
  }

  then<TResult1 = QueryResult<T[]>, TResult2 = never>(
    onfulfilled?: ((value: QueryResult<T[]>) => TResult1 | PromiseLike<TResult1>) | null,
    onrejected?: ((reason: unknown) => TResult2 | PromiseLike<TResult2>) | null,
  ): PromiseLike<TResult1 | TResult2> {
    return this.execute<T[]>().then(onfulfilled, onrejected);
  }

  private async execute<R>(): Promise<QueryResult<R>> {
    const { data, error } = await request<{ data: R; count?: number }>("/query", {
      method: "POST",
      body: JSON.stringify({
        table: this.table,
        method: this.writeMethod,
        select: this.selectColumns,
        filters: this.filters,
        orders: this.orders,
        values: this.values,
        head: this.selectOptions.head,
      }),
    });
    if (error) return { data: null, error, count: null };
    return { data: data?.data ?? null, error: null, count: data?.count ?? null };
  }
}

export const localBackend = {
  from(table: string) {
    return new QueryBuilder(table);
  },
  auth: {
    onAuthStateChange(callback: AuthCallback) {
      listeners.add(callback);
      return { data: { subscription: { unsubscribe: () => listeners.delete(callback) } } };
    },
    async getSession(): Promise<{ data: { session: LocalSession | null }; error: null }> {
      const local = storedSession();
      if (!local) return { data: { session: null }, error: null };
      const { data, error } = await request<{ session: LocalSession | null }>("/auth/session", { method: "POST", body: "{}" });
      if (error || !data?.session) {
        setStoredSession(null);
        return { data: { session: null }, error: null };
      }
      setStoredSession(data.session);
      return { data: { session: data.session }, error: null };
    },
    async signInWithPassword(credentials: { email: string; password: string }) {
      const { data, error } = await request<{ session: LocalSession }>("/auth/signin", { method: "POST", body: JSON.stringify(credentials) });
      if (!error && !data?.session?.access_token) {
        return { data: null, error: { message: "Sign-in did not return a session" } };
      }
      if (data?.session) setStoredSession(data.session);
      return { data, error };
    },
    async signUp(credentials: { email: string; password: string; options?: unknown }) {
      const { data, error } = await request<{ user: LocalUser }>("/auth/signup", { method: "POST", body: JSON.stringify(credentials) });
      return { data, error };
    },
    async signOut() {
      const result = await request("/auth/signout", { method: "POST", body: "{}" });
      setStoredSession(null);
      return result;
    },
    async updateUser(values: { password?: string }) {
      return request<{ user: LocalUser }>("/auth/update", { method: "POST", body: JSON.stringify(values) });
    },
  },
  async downloadFormAttachment(submissionId: string, attachmentIndex: number): Promise<{ data: Blob | null; error: { message: string } | null }> {
    if (typeof window === "undefined") return { data: null, error: { message: "Downloads are only available in the browser" } };
    try {
      const session = storedSession();
      const headers = new Headers();
      if (session?.access_token) headers.set("authorization", `Bearer ${session.access_token}`);
      const response = await fetch(`${API_BASE}/form-attachments/${encodeURIComponent(submissionId)}/${attachmentIndex}`, { headers });
      if (!response.ok) {
        const data = await response.json().catch(() => null);
        return { data: null, error: { message: data?.error || "Could not download attachment" } };
      }
      return { data: await response.blob(), error: null };
    } catch (error) {
      return { data: null, error: { message: error instanceof Error ? error.message : "Could not download attachment" } };
    }
  },
  storage: {
    from(bucket: string) {
      return {
        async upload(path: string, file: File, _options?: { upsert?: boolean }) {
          const arrayBuffer = await file.arrayBuffer();
          return request<{ path: string }>(`/storage/${bucket}/${encodeURIComponent(path)}`, { method: "POST", body: arrayBuffer });
        },
        getPublicUrl(path: string) {
          return { data: { publicUrl: `${API_BASE}/media/${encodeURIComponent(path)}` } };
        },
      };
    },
  },
  functions: {
    async invoke(name: string, options: { body?: Record<string, unknown> } = {}) {
      return request(`/${"functions"}/${name}`, { method: "POST", body: JSON.stringify(options.body ?? {}) });
    },
  },
};
